import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoIcon from '@mui/icons-material/Info';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useImmer } from 'use-immer';
import { ClickEvent } from '../../../../common-types';
import { UndefinedDisplayKey } from '../../../common/api/api-interfaces';
import { SparkChartComponent } from '../../../common/components/charts/SparkChartComponent';
import { FixedWidthGap, Flex } from '../../../common/components/layout';
import ServiceExecutorWithRoles from '../../../common/components/query/ServiceExecutorWithRoles';
import { BALANCE_TABLE_ROOT_ELEMENT } from '../../../common/components/share-export/utils';
import {
  getSelectMenuItems,
  InvisibleSelect,
} from '../../../common/components/utils/Select';
import {
  MetricCategoryId,
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import { getIntercomClassNameForMetric } from '../../../common/intercom/intercom-utils';
import { Service } from '../../../common/services/utils';
import {
  bodyBackground,
  darkTeal,
  mediumGray,
  pbsHeaderBorderCss,
  pbsRowHoverColorLevel1,
  teal2,
  unselectedFactboxColor,
} from '../../../common/theme/color-constants';
import {
  getAliasForMetricCategoryId,
  getAliasForMetricGroupId,
} from '../../../common/v2/api/alias/utils';
import {
  useAliasesContext,
  useEffectiveRoleViewAuthorizationServiceContext,
  useGlobalLocaleContext,
  useIntercomStoreContext,
  useLegacyDomainScopedStoresContext,
} from '../../../common/v2/context/contexts';
import {
  GlobalFilterHandle,
  GlobalSegmentHandle,
} from '../../../common/v2/context/types';
import { BalanceSheetStore } from './balance-sheet-store';
import { PbsSegmentationRows } from './BalanceSheetSegmentationRows';
import { MetricQueryMetaData } from './MetricQueryMetaData';
import { MetricTooltip, PopoverData } from './MetricTooltip';
import { OpenMetricTooltipIcon } from './OpenMetricTooltipIcon';
import {
  getFormattedValForBenchmarkHeader,
  getFormattedValForFixedHeader,
  getFormattedValForTimeHeader,
} from './services/pbs-utils';
import {
  getDisplayMetricCategoryName,
  getDisplayMetricGroupName,
  periodTrend,
} from './translations';
import {
  BalanceSheetTableServiceInputs,
  BalanceSheetTableServiceResponse,
  MetaDataObject,
  PbsCategoryDropdownItem,
  PbsServiceResponseData,
  PbsTableHeaderObj,
  PERIOD_TOTAL,
} from './types';

export interface PbsTableProps {
  balanceSheetStore: BalanceSheetStore;
  filterHandle: GlobalFilterHandle;
  segmentationLevel1Handle: GlobalSegmentHandle;
  segmentationLevel2Handle: GlobalSegmentHandle;
}

const StyledGrid = styled(Grid)`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const StyledSegParentGrid = styled('div')<{
  $isSegmented: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-left: ${(props) => (props.$isSegmented ? '0.3em' : 0)};
  cursor: pointer;
`;

const StyledTable = styled(Table)`
  width: auto;
  table-layout: fixed;
  height: 100%;
  contain: paint;

  & .MuiTableCell-stickyHeader {
    background-color: ${bodyBackground};
    position: sticky !important;
  }
  /* TODO: cell-widths are not great right now.
  Ideally they need to adapt dynamically acc to the content, or maybe we set colspans. */
`;

const InfoIconContainer = styled(Grid)`
  position: absolute;
  top: 32%;
  right: 0;
  display: none;
  z-index: 1;
`;

const IntercomIcon = styled(InfoIcon)`
  color: ${mediumGray};
  font-size: 1.4em;
  cursor: pointer;
  &:hover {
    color: ${teal2};
  }
`;

export const StyledTableInfoIcon = styled(InfoIcon)`
  color: ${mediumGray};
  font-size: 1.4em;
  cursor: pointer;
  &:hover {
    color: ${teal2};
  }
`;

export const MetaDataIcon = styled(StyledTableInfoIcon)`
  visibility: hidden;
`;

export const StyledTableCell = styled(TableCell)<{
  $color?: string;
  $borderRight?: string;
  $canBeHighlighted?: boolean;
  $isBenchmark?: boolean;
}>`
  color: ${(props) => props.$color ?? 'inherit'};
  border-right: ${(props) => props.$borderRight ?? 'none'};
  border-bottom: none;
  padding: 5px 0.5em 5px 0;
  font-size: 13px;
  position: relative;
  background-color: ${(props) =>
    props.$isBenchmark ? `${darkTeal} !important` : 'unset'};

  &:last-child {
    padding-right: 24px;
  }

  &:not(:empty):hover::after {
    content: '';
    height: 10000px;
    left: 0;
    position: absolute;
    top: -5000px;
    width: 100%;
    z-index: -1;
  }

  &:hover::after {
    background-color: ${(props) =>
      props.$canBeHighlighted ? pbsRowHoverColorLevel1 : 'unset'};
  }
`;

const StyledTypography = styled(Typography)`
  font-weight: 600;
`;

const SubTypography = styled(Typography)`
  cursor: pointer;
  :hover {
    color: ${unselectedFactboxColor};
    font-weight: 600;
  }
`;

export const StyledTableRow = styled(TableRow)`
  height: 0;
  &:hover {
    background-color: ${pbsRowHoverColorLevel1};
    ${InfoIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

const StyledIconButton = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  color: ${darkTeal};
  cursor: pointer;
`;

const StyledLink = styled(Link as any)`
  text-decoration: none;
  color: inherit;
  padding-left: 1em;

  :hover {
    color: ${unselectedFactboxColor};
  }
`;

export const renderPbsTableCell = (
  metricGroupId: MetricGroupId,
  headerVal: string,
  cellVal: number | string
) => {
  return (
    <StyledTableCell
      align="right"
      key={`${metricGroupId}${headerVal}${cellVal}`}
      $canBeHighlighted
    >
      {cellVal}
    </StyledTableCell>
  );
};

export type CellValGetter = (
  dataToUse: PbsServiceResponseData,
  timeHeaderVal: string,
  metricId: MetricId
) => number | null;

export const BalanceSheetTable = observer((props: PbsTableProps) => {
  const aliases = useAliasesContext();
  const locale = useGlobalLocaleContext();
  const selectedLanguage = locale.selected;
  const intercomStore = useIntercomStoreContext();
  const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const { metricStore, timeSliderStore, benchmarkStore } =
    legacyDomainScopedStores;
  const [metaDataObject, setMetaDataObject] = useState<MetaDataObject | null>(
    null
  );
  const showMetaData = authorizationService.canManageAllDomains();
  const [newPopoverAnchorEl, setNewPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [isNewPopoverOpen, setIsNewPopoverOpen] = useState(false);
  const [currentPopoverDataForNewTooltip, setCurrentPopoverDataForNewTooltip] =
    useState<PopoverData | null>(null);

  const handleOpenNewMetricTooltip = (
    event: React.MouseEvent<HTMLElement>,
    data: PopoverData
  ) => {
    setNewPopoverAnchorEl(event.currentTarget);
    setCurrentPopoverDataForNewTooltip(data);
    setIsNewPopoverOpen(true);
  };

  const handleCloseNewMetricTooltip = () => {
    setNewPopoverAnchorEl(null);
    setCurrentPopoverDataForNewTooltip(null);
    setIsNewPopoverOpen(false);
  };

  const sparkChart = (
    dateValueObjs: Record<string, number>,
    metaDataObject: MetaDataObject | null
  ) => {
    return (
      <StyledTableCell
        style={{ paddingLeft: '1em' }}
        align="right"
        $canBeHighlighted
      >
        <SparkChartComponent
          dateValueObjs={dateValueObjs}
          customWidth={'3vw'}
        />
        {showMetaData && metaDataObject && (
          <Tooltip
            title={'Show Sql'}
            placement="top"
            onClick={(e: ClickEvent) => {
              e.stopPropagation();
              setMetaDataObject(metaDataObject);
            }}
          >
            <MetaDataIcon />
          </Tooltip>
        )}
      </StyledTableCell>
    );
  };
  const { t } = useTranslation();
  const {
    balanceSheetStore,
    filterHandle,
    segmentationLevel1Handle,
    segmentationLevel2Handle,
  } = props;
  const {
    dropdownValues,
    selectedPbsValues,
    segmentedSubVal,
    setSegmentedSubVal,
    setSelectedMeasureOnPbsItem,
    setMetricChart,
    rowInfoLink,
    balanceSheetTableService,
  } = balanceSheetStore;
  const isSegmentationEnabled = true;
  const [segmentationLevel1State] = segmentationLevel1Handle;
  const [filtersState] = filterHandle;
  const { items: filters } = filtersState;
  const { timeSliderConfig } = timeSliderStore;
  const { selectedBenchmark } = benchmarkStore;

  const getDropdownObjForRowGroup = (rowGroupName: MetricCategoryId) => {
    return dropdownValues.find(
      ({ value }) => rowGroupName === value
    ) as PbsCategoryDropdownItem;
  };

  const MetricCategories = dropdownValues.map((v) => v.value);
  const [rowGroupExpanded, setRowGroupExpanded] = useImmer(
    Object.fromEntries(MetricCategories.map((c) => [c, true]))
  );

  const toggleRowGroupExpanded = (rowGroup: MetricCategoryId) => {
    setRowGroupExpanded((draft) => {
      draft[rowGroup] = !draft[rowGroup];
    });
  };

  const renderPbsTableHeader = (
    { value, label, subVal }: PbsTableHeaderObj,
    isBenchmark = false
  ) => {
    return (
      <StyledTableCell
        key={`${label}${value}${subVal ?? t(UndefinedDisplayKey)}${isBenchmark.toString()}`}
        align="right"
        $color={isBenchmark ? 'white' : darkTeal}
        $borderRight={isBenchmark ? '1px solid white' : pbsHeaderBorderCss}
        $isBenchmark={isBenchmark}
        $canBeHighlighted
      >
        <Grid
          item
          container
          wrap="nowrap"
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'inherit',
            }}
          >
            {label}
          </Typography>
          <FixedWidthGap $gap="4px" />
          {value === PERIOD_TOTAL ? (
            <Tooltip
              title={t(
                `pages:dashboards.peopleBalanceSheet.table.periodTotalTooltip`
              )}
              placement="top"
              onClick={(e: ClickEvent) => {
                e.stopPropagation();
                intercomStore.showArticleInIntercom(value);
              }}
            >
              <StyledTableInfoIcon />
            </Tooltip>
          ) : (
            ''
          )}
        </Grid>
        {subVal ? <Grid item>{subVal}</Grid> : null}
      </StyledTableCell>
    );
  };
  const inputs = {
    timeSliderConfig,
    selectedPbsValues,
    selectedBenchmark,
  };

  return (
    <>
      <MetricTooltip
        open={isNewPopoverOpen}
        anchorEl={newPopoverAnchorEl}
        onClose={handleCloseNewMetricTooltip}
        popoverData={currentPopoverDataForNewTooltip}
      />
      <ServiceExecutorWithRoles
        inputs={toJS(inputs, { recurseEverything: true })}
        filters={toJS(filters)}
        service={
          balanceSheetTableService as unknown as Service<
            BalanceSheetTableServiceInputs,
            BalanceSheetTableServiceResponse
          >
        }
      >
        {({ data }) => {
          if (!data) return null;
          const {
            fixedHeaderObjs,
            timeHeaderObjs,
            benchmarkHeaderObjs,
            tableData,
            meta,
          } = data;
          return (
            <StyledGrid id={BALANCE_TABLE_ROOT_ELEMENT}>
              <StyledTable stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '15%' }} />
                    <StyledTableCell style={{ width: '10%' }} />
                    {timeHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj)
                    )}
                    <StyledTableCell
                      style={{ width: '5%' }}
                      align="right"
                      $color={darkTeal}
                      $borderRight={pbsHeaderBorderCss}
                      $canBeHighlighted
                    >
                      <Grid item>
                        <Typography
                          sx={{
                            color: 'inherit',
                          }}
                        >
                          {periodTrend}
                        </Typography>
                      </Grid>
                    </StyledTableCell>
                    {fixedHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj)
                    )}
                    {benchmarkHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj, true)
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.rowGroups.map(({ metricCategory, rows }) => {
                    // TODO: Make translation handling better
                    const metricCatetoryAlias = getAliasForMetricCategoryId(
                      aliases,
                      selectedLanguage
                    )(metricCategory);
                    const rowGroupLabel = getDisplayMetricCategoryName(
                      metricCategory,
                      metricCatetoryAlias,
                      metricStore
                    );
                    const dropdownObj =
                      getDropdownObjForRowGroup(metricCategory);
                    const isExpanded = rowGroupExpanded[metricCategory];
                    const numSelected = dropdownObj.subItems.filter(
                      (sub) => sub.selected
                    ).length;
                    const numTotal = dropdownObj.subItems.length;
                    return (
                      <Fragment key={`balancesheettable-${metricCategory}`}>
                        {
                          <StyledTableRow
                            key={metricCategory}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              toggleRowGroupExpanded(metricCategory)
                            }
                            data-cy="pbs-table-header-row"
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              $color={darkTeal}
                              style={{ maxWidth: '16em' }}
                            >
                              <Flex>
                                <StyledIconButton>
                                  {isExpanded ? (
                                    <ArrowDropUpIcon />
                                  ) : (
                                    <ArrowDropDownIcon />
                                  )}
                                </StyledIconButton>
                                <StyledTypography color="inherit">{`${rowGroupLabel} (${numSelected}/${numTotal})`}</StyledTypography>
                              </Flex>
                            </StyledTableCell>
                            <StyledTableCell align="right" />
                            {timeHeaderObjs.map(
                              ({ value: headerValue, label }) => {
                                return (
                                  <StyledTableCell
                                    align="right"
                                    $canBeHighlighted
                                    key={`${metricCategory}${headerValue}${label}`}
                                  />
                                );
                              }
                            )}
                            <StyledTableCell
                              style={{ width: '5%' }}
                              align="right"
                              $canBeHighlighted
                            />
                            {[...fixedHeaderObjs, ...benchmarkHeaderObjs].map(
                              ({ value: headerValue, label }) => {
                                return (
                                  <StyledTableCell
                                    align="right"
                                    $canBeHighlighted
                                    key={`${metricCategory}${headerValue}${label}`}
                                  />
                                );
                              }
                            )}
                          </StyledTableRow>
                        }
                        {isExpanded &&
                          rows.map(({ metricGroupId, data }) => {
                            const { metricsToSelectedMetricIdsMap } =
                              balanceSheetStore;
                            const metricGroupIdAlias = getAliasForMetricGroupId(
                              aliases,
                              selectedLanguage
                            )(metricGroupId);
                            const metrics =
                              metricStore.getAllowedSortedMetricIdsForMetricGroup(
                                metricGroupId
                              );
                            const measures = metrics.map((m) => {
                              return {
                                label: metricStore.getTranslatedMetricIdName(
                                  m.id as MetricId
                                ),
                                value: m.id,
                                key: m.id,
                              };
                            });
                            const selectedMeasure =
                              metricsToSelectedMetricIdsMap?.[metricGroupId];
                            if (!selectedMeasure) {
                              throw new Error(
                                `no selected measure for dimension - ${metricGroupId}`
                              );
                            }

                            const selectedMetricDef = metrics.find(
                              (def) => def.id === selectedMeasure
                            );

                            const dateValueObjs: Record<string, number> = {};
                            data[metricGroupId].timeHeadersData.forEach((t) => {
                              const date = t.dateRange[1];
                              const valueForDate = t.data?.[selectedMeasure];
                              dateValueObjs[date] = valueForDate ?? 0;
                            });
                            const currMetaDataObject =
                              meta?.[metricGroupId]?.[selectedMeasure]
                                ?.overtime ?? null;
                            return (
                              <Fragment key={metricGroupId}>
                                <StyledTableRow
                                  onClick={() => {
                                    const canSegment =
                                      !!segmentationLevel1State.items.length &&
                                      isSegmentationEnabled;
                                    if (canSegment) {
                                      segmentedSubVal === metricGroupId
                                        ? setSegmentedSubVal(null)
                                        : setSegmentedSubVal(metricGroupId);
                                    }
                                  }}
                                  style={
                                    !!segmentationLevel1State.items.length &&
                                    isSegmentationEnabled
                                      ? {
                                          cursor: 'pointer',
                                        }
                                      : {}
                                  }
                                  data-cy="pbs-table-value-row"
                                >
                                  <StyledTableCell
                                    component="th"
                                    scope="row"
                                    $color={darkTeal}
                                    style={{ maxWidth: '16rem' }}
                                  >
                                    <StyledSegParentGrid $isSegmented={true}>
                                      {segmentationLevel1State.items.length &&
                                      isSegmentationEnabled ? (
                                        <>
                                          {segmentedSubVal === metricGroupId ? (
                                            <div
                                              onClick={() =>
                                                setSegmentedSubVal(null)
                                              }
                                              style={{ paddingTop: '7px' }}
                                            >
                                              <ArrowDropUpIcon
                                                color="action"
                                                style={{ fontSize: '2.5em' }}
                                                data-cy="pbs-segment-render-arrow-up"
                                              />
                                            </div>
                                          ) : (
                                            <div
                                              onClick={() =>
                                                setSegmentedSubVal(
                                                  metricGroupId
                                                )
                                              }
                                              style={{ paddingTop: '5px' }}
                                            >
                                              <ArrowDropDownIcon
                                                color="action"
                                                style={{ fontSize: '2.5em' }}
                                                data-cy="pbs-segment-render-arrow-down"
                                              />
                                            </div>
                                          )}
                                        </>
                                      ) : null}
                                      <StyledLink
                                        to={rowInfoLink}
                                        onClick={(e: ClickEvent) => {
                                          e.stopPropagation();
                                          setMetricChart({
                                            label: getDisplayMetricGroupName(
                                              metricGroupId,
                                              metricGroupIdAlias,
                                              metricStore
                                            ),
                                            metricGroupId,
                                            metricIds: measures,
                                          });
                                        }}
                                      >
                                        <SubTypography>
                                          {getDisplayMetricGroupName(
                                            metricGroupId,
                                            metricGroupIdAlias,
                                            metricStore
                                          )}{' '}
                                        </SubTypography>
                                      </StyledLink>
                                    </StyledSegParentGrid>
                                    <InfoIconContainer>
                                      {showMetaData && currMetaDataObject && (
                                        <Tooltip
                                          title={'Show Sql'}
                                          placement="top"
                                          onClick={(e: ClickEvent) => {
                                            e.stopPropagation();
                                            setMetaDataObject(
                                              currMetaDataObject
                                            );
                                          }}
                                        >
                                          <MetaDataIcon />
                                        </Tooltip>
                                      )}
                                    </InfoIconContainer>
                                  </StyledTableCell>
                                  <StyledTableCell
                                    align="right"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <InvisibleSelect
                                        value={selectedMeasure}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setNewPopoverAnchorEl(null);
                                          setIsNewPopoverOpen(false);
                                          setCurrentPopoverDataForNewTooltip(
                                            null
                                          );
                                          setSelectedMeasureOnPbsItem(
                                            metricGroupId,
                                            e.target.value as MetricId
                                          );
                                        }}
                                        className={(() => {
                                          const intercomClassname =
                                            getIntercomClassNameForMetric(
                                              selectedMeasure
                                            );
                                          return intercomClassname;
                                        })()}
                                      >
                                        {getSelectMenuItems(
                                          measures.map((m) => {
                                            const intercomClassname = m.value
                                              ? getIntercomClassNameForMetric(
                                                  m.value
                                                )
                                              : '';
                                            return {
                                              ...m,
                                              className: intercomClassname,
                                            };
                                          })
                                        )}
                                      </InvisibleSelect>
                                      <OpenMetricTooltipIcon
                                        metric={{
                                          metricCategoryName:
                                            getDisplayMetricCategoryName(
                                              metricCategory,
                                              metricCatetoryAlias,
                                              metricStore
                                            ),
                                          metricGroupName:
                                            getDisplayMetricGroupName(
                                              metricGroupId,
                                              metricGroupIdAlias,
                                              metricStore
                                            ),
                                          metricName:
                                            metricStore.getTranslatedMetricIdName(
                                              selectedMeasure
                                            ),
                                          tooltipJapanese:
                                            selectedMetricDef?.tooltipJapanese ??
                                            null,
                                          tooltipEnglish:
                                            selectedMetricDef?.tooltipEnglish ??
                                            null,
                                        }}
                                        onOpen={handleOpenNewMetricTooltip}
                                      />
                                    </div>
                                  </StyledTableCell>
                                  {timeHeaderObjs.map(
                                    ({ value: timeHeaderVal }) => {
                                      const formattedVal =
                                        getFormattedValForTimeHeader(
                                          timeHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      return renderPbsTableCell(
                                        metricGroupId,
                                        timeHeaderVal,
                                        formattedVal
                                      );
                                    }
                                  )}
                                  {sparkChart(
                                    dateValueObjs,
                                    meta?.[metricGroupId]?.[selectedMeasure]
                                      ?.periodTotal ?? null
                                  )}
                                  {fixedHeaderObjs.map(
                                    ({ value: fixedHeaderVal }) => {
                                      const formattedVal =
                                        getFormattedValForFixedHeader(
                                          fixedHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      return renderPbsTableCell(
                                        metricGroupId,
                                        fixedHeaderVal,
                                        formattedVal
                                      );
                                    }
                                  )}
                                  {benchmarkHeaderObjs.map(
                                    ({ value: benchmarkHeaderVal }) => {
                                      const cellData =
                                        data[metricGroupId]
                                          .benchmarkHeadersData?.[
                                          benchmarkHeaderVal
                                        ];
                                      if (!cellData) return null;
                                      const formattedVal =
                                        getFormattedValForBenchmarkHeader(
                                          benchmarkHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      const currMetaDataObject =
                                        meta?.[metricGroupId]?.[selectedMeasure]
                                          .benchmark ?? null;
                                      return (
                                        <StyledTableCell
                                          align="right"
                                          key={`${benchmarkHeaderVal}${formattedVal}`}
                                          $canBeHighlighted
                                        >
                                          {showMetaData &&
                                            currMetaDataObject && (
                                              <Tooltip
                                                title={'Show Sql'}
                                                placement="top"
                                                onClick={(e: ClickEvent) => {
                                                  e.stopPropagation();
                                                  setMetaDataObject(
                                                    currMetaDataObject
                                                  );
                                                }}
                                              >
                                                <MetaDataIcon />
                                              </Tooltip>
                                            )}
                                          {formattedVal}
                                        </StyledTableCell>
                                      );
                                    }
                                  )}
                                </StyledTableRow>
                                {segmentationLevel1State.items.length &&
                                segmentedSubVal === metricGroupId &&
                                isSegmentationEnabled ? (
                                  <PbsSegmentationRows
                                    metric={metricGroupId}
                                    selectedDimension={selectedMeasure}
                                    balanceSheetStore={balanceSheetStore}
                                    openSqlModal={(
                                      metaDataObject: MetaDataObject
                                    ) => {
                                      setMetaDataObject(metaDataObject);
                                    }}
                                    filterHandle={filterHandle}
                                    segmentationLevel1Handle={
                                      segmentationLevel1Handle
                                    }
                                    segmentationLevel2Handle={
                                      segmentationLevel2Handle
                                    }
                                  />
                                ) : null}
                              </Fragment>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </StyledGrid>
          );
        }}
      </ServiceExecutorWithRoles>
      <MetricQueryMetaData
        open={!!metaDataObject}
        metaDataObject={metaDataObject}
        onClose={() => setMetaDataObject(null)}
      />
    </>
  );
});
